import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Grid,
  Paper,
  LinearProgress,
  Chip,
  Alert,
  Collapse,
  IconButton,
  Tooltip,
  Table,
  Skeleton,
  Fade,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Radio,
  TableSortLabel
} from '@mui/material';
import { RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar, ResponsiveContainer, Tooltip as RechartsTooltip } from 'recharts';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import TimelineIcon from '@mui/icons-material/Timeline';
import BarChartIcon from '@mui/icons-material/BarChart';
import SearchIcon from '@mui/icons-material/Search';
import GroupIcon from '@mui/icons-material/Group';
import TuneIcon from '@mui/icons-material/Tune';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import { useTranslation } from 'react-i18next';
import SensitivityBadge from './SensitivityBadge';
import { getParameterLookupId } from '../../services/catalogService';
import BoxplotZeile from '../../pages/olymp/data_catalog/components/BoxplotZeile';

/** Prozent lesbar, wie in ExpandedRow.js (#264): eine Nachkommastelle, keine
 *  ueberfluessige Null bei Ganzzahlen. */
const formatiereProzent = (wert) =>
  Number.isFinite(wert) ? String(Math.round(wert * 10) / 10) : '';

const DATA_EXPLORATION_COPY = {
  de: {
    heroTitle: 'Verfügbarkeit & Nutzung',
    cases: 'Fälle',
    coverage: 'Abdeckung',
    stationGroups: 'Stationsgruppen',
    availableSince: 'Verfügbar seit',
    qualityProfile: 'Qualitätsprofil',
    quality: 'Qualität',
    activeFilter: 'Aktiver Filter:',
    followingViewsFiltered: 'Die Ähnlichen Parameter sind auf diesen Bereich gefiltert',
    filterEnabled: 'Filter aktiviert!',
    filterEnabledBody:
      'Die Sektion Ähnliche Parameter zeigt jetzt ausschließlich Daten für {{group}}. Klicken Sie auf das ×-Symbol im Filter-Chip, um zur Gesamtansicht zurückzukehren.',
    availabilityByArea: 'Datenfilter: Verfügbarkeit nach Bereich',
    privacyTooltip: 'Einzelne Stationen werden nur angezeigt, wenn mindestens 30 Fälle vorliegen (Datenschutz)',
    selectAreaPrompt:
      'Wählen Sie einen Bereich aus, um die Ähnlichen Parameter auf diesen Bereich zu filtern.',
    area: 'Bereich',
    stations: 'Stationen',
    details: 'Details',
    allAreas: 'Alle Bereiche',
    noStationsMeetMinimum: 'Keine Stationen erfüllen das Mindestkriterium von 30 Fällen (Datenschutz)',
    stationsInGroup: 'Stationen in {{group}}',
    usageCharacteristics: 'Nutzungs-Charakteristik',
    basedOnAll: 'Daten basieren auf allen Bereichen (aggregiert)',
    measurementFrequency: 'Messfrequenz',
    mostCommon: 'Häufigste',
    median: 'Median',
    spread: 'Streuung',
    noData: 'Keine Daten verfügbar',
    measurementsPerCase: 'Messungen pro Fall',
    statsBasedOnCases: 'Statistik basiert auf {{count}} Fällen mit mindestens einer Messung',
    averageShort: 'Durchschn.',
    total: 'Gesamt',
    continuousInfo: '{{percent}}% der Fälle haben ≥10 Messungen (kontinuierlich)',
    methodology:
      'Methodologie: Alle Statistiken basieren auf Fällen mit mindestens einer Messung ({{count}} Fälle). Messfrequenz-Statistiken beziehen sich auf {{group}}.',
    mainUsageArea: 'die Hauptnutzungsbereiche',
    dataQuality: 'Datenqualität & Validierung',
    unitsInData: 'Einheiten in den Daten:',
    outlierHandling: 'Outlier-Behandlung',
    important: 'Wichtig:',
    similarParameters: 'Parameter mit ähnlicher Erfassung',
    similarParametersTooltip: 'Parameter mit ähnlicher Messfrequenz, Stationsnutzung und Erfassungsmuster',
    similarParametersSummary:
      'Diese Parameter werden mit ähnlicher Häufigkeit und auf ähnlichen Stationen erfasst (unabhängig vom medizinischen Inhalt)',
    noSimilarParameters: 'Für diesen Filter sind noch keine ähnlichen Parameter hinterlegt.',
    similarityReasons: 'Ähnlichkeitsgründe',
    frequency: 'Frequenz',
    similarity: 'Ähnlichkeit',
    similarityNote:
      'Hinweis: Die Ähnlichkeit bezieht sich auf die Erfassungseigenschaften (Messfrequenz, Stationsnutzung, Erfassungsmuster), nicht auf die medizinische Bedeutung.',
    similarFrequencyLabels: {
      hourly: 'Stündlich',
      continuous: 'Kontinuierlich',
      fourHourly: '4-stündlich',
      twelveToTwentyFour: '12-24h',
      irregular: 'Unregelmäßig',
      continuousBeatmet: 'Kontinuierlich',
      daily: 'Täglich',
      everyTwoToThreeDays: 'Alle 2-3 Tage',
      everyFourToSixHours: '4-6 Stunden',
      asNeeded: 'Bei Bedarf',
      unknown: 'Unbekannt',
    },
    measurementPatternInterpretations: {
      noUsageData:
        'Neu hinzugefügter Parameter, noch keine Nutzungsdaten verfügbar.',
    },
    similarityReasonLabels: {
      sameMeasurementFrequency: 'Gleiche Messfrequenz',
      similarStationUse: 'Ähnliche Stationsnutzung',
      continuousCapture: 'Kontinuierliche Erfassung',
      vitalSign: 'Vitalparameter',
      relatedMeasurement: 'Zusammengehörige Messung',
      regularCapture: 'Regelmäßige Erfassung',
      identicalStationUse: 'Identische Stationsnutzung',
      identicalIcuStations: 'Identische ICU-Stationen',
      sameFrequency: 'Gleiche Frequenz',
      identicalMonitoringSystem: 'Identisches Monitoring-System',
      icuVitalSign: 'ICU-Vitalparameter',
      icuStandard: 'ICU-Standard',
      imcVitalSign: 'IMC-Vitalparameter',
      imcStandard: 'IMC-Standard',
      identicalImcStations: 'Identische IMC-Stationen',
      basicVitalSign: 'Basisvitalparameter',
      stationBreadth: 'Stationsbreite',
      generalWardRoutine: 'Normalstations-Routine',
      intraOpStandard: 'Intraop-Standard',
      identicalOperatingRooms: 'Identische OP-Säle',
      anesthesiaRequired: 'Anästhesie-Pflicht',
      labValue: 'Laborwert',
      similarSamplingFrequency: 'Ähnliche Abnahmefrequenz',
      electrolytePanel: 'Elektrolyt-Panel',
    },
    stationGroupLabels: {
      icu: 'Intensivstationen',
      imc: 'IMC (Intermediate Care)',
      ward: 'Normalstationen',
      opDiagnostics: 'OP & Diagnostik',
      lab: 'Labor',
      other: 'Sonstige',
    },
    radar: {
      caseCoverage: 'Fallabdeckung',
      availability: 'Verfügbarkeit',
      stationBreadth: 'Stationsbreite',
      continuity: 'Kontinuität',
      caseCoverageDescription: 'Prozentsatz der Fälle mit mindestens einer Messung',
      availabilityDescription: 'Seit {{years}} Jahren verfügbar (max. 10 Jahre = 100%)',
      stationBreadthDescription: 'Auf {{count}} Stationsgruppen verfügbar (6+ = 100%)',
      continuityDescription: 'Prozentsatz der Fälle mit ≥10 Messungen (kontinuierliche Erfassung)',
    },
  },
  en: {
    heroTitle: 'Availability & usage',
    cases: 'Cases',
    coverage: 'Coverage',
    stationGroups: 'Station groups',
    availableSince: 'Available since',
    qualityProfile: 'Quality profile',
    quality: 'Quality',
    activeFilter: 'Active filter:',
    followingViewsFiltered: 'The Similar parameters section is filtered to this area',
    filterEnabled: 'Filter enabled!',
    filterEnabledBody:
      'The Similar parameters section now shows data only for {{group}}. Click the × icon in the filter chip to return to the overall view.',
    availabilityByArea: 'Data filter: availability by area',
    privacyTooltip: 'Individual stations are shown only when at least 30 cases are available (privacy protection)',
    selectAreaPrompt:
      'Select an area to filter the similar parameters to that area.',
    area: 'Area',
    stations: 'Stations',
    details: 'Details',
    allAreas: 'All areas',
    noStationsMeetMinimum: 'No stations meet the minimum threshold of 30 cases (privacy protection)',
    stationsInGroup: 'Stations in {{group}}',
    usageCharacteristics: 'Usage characteristics',
    basedOnAll: 'Data is based on all areas (aggregated)',
    measurementFrequency: 'Measurement frequency',
    mostCommon: 'Most common',
    median: 'Median',
    spread: 'Spread',
    noData: 'No data available',
    measurementsPerCase: 'Measurements per case',
    statsBasedOnCases: 'Statistics based on {{count}} cases with at least one measurement',
    averageShort: 'Avg.',
    total: 'Total',
    continuousInfo: '{{percent}}% of cases have ≥10 measurements (continuous)',
    methodology:
      'Methodology: All statistics are based on cases with at least one measurement ({{count}} cases). Measurement-frequency statistics refer to {{group}}.',
    mainUsageArea: 'the main usage areas',
    dataQuality: 'Data quality & validation',
    unitsInData: 'Units in the data:',
    outlierHandling: 'Outlier handling',
    important: 'Important:',
    similarParameters: 'Parameters with similar capture behavior',
    similarParametersTooltip: 'Parameters with similar measurement frequency, station usage, and capture pattern',
    similarParametersSummary:
      'These parameters are captured with similar frequency and in similar stations (independent of medical meaning)',
    noSimilarParameters: 'No similar parameters are stored for this filter yet.',
    similarityReasons: 'Similarity reasons',
    frequency: 'Frequency',
    similarity: 'Similarity',
    similarityNote:
      'Note: similarity refers to capture characteristics (measurement frequency, station usage, capture pattern), not medical meaning.',
    similarFrequencyLabels: {
      hourly: 'Hourly',
      continuous: 'Continuous',
      fourHourly: 'Every 4 hours',
      twelveToTwentyFour: '12-24h',
      irregular: 'Irregular',
      continuousBeatmet: 'Continuous (ventilated)',
      daily: 'Daily',
      everyTwoToThreeDays: 'Every 2-3 days',
      everyFourToSixHours: 'Every 4-6 hours',
      asNeeded: 'As needed',
      unknown: 'Unknown',
    },
    measurementPatternInterpretations: {
      noUsageData:
        'Newly added parameter; no usage data available yet.',
    },
    similarityReasonLabels: {
      sameMeasurementFrequency: 'Same measurement frequency',
      similarStationUse: 'Similar station usage',
      continuousCapture: 'Continuous capture',
      vitalSign: 'Vital sign',
      relatedMeasurement: 'Related measurement',
      regularCapture: 'Regular capture',
      identicalStationUse: 'Identical station usage',
      identicalIcuStations: 'Identical ICU stations',
      sameFrequency: 'Same frequency',
      identicalMonitoringSystem: 'Identical monitoring system',
      icuVitalSign: 'ICU vital sign',
      icuStandard: 'ICU standard',
      imcVitalSign: 'IMC vital sign',
      imcStandard: 'IMC standard',
      identicalImcStations: 'Identical IMC stations',
      basicVitalSign: 'Basic vital sign',
      stationBreadth: 'Station breadth',
      generalWardRoutine: 'General-ward routine',
      intraOpStandard: 'Intraoperative standard',
      identicalOperatingRooms: 'Identical operating rooms',
      anesthesiaRequired: 'Anaesthesia-required',
      labValue: 'Lab value',
      similarSamplingFrequency: 'Similar sampling frequency',
      electrolytePanel: 'Electrolyte panel',
    },
    stationGroupLabels: {
      icu: 'Intensive care units',
      imc: 'IMC (Intermediate Care)',
      ward: 'General wards',
      opDiagnostics: 'OR & Diagnostics',
      lab: 'Lab',
      other: 'Other',
    },
    radar: {
      caseCoverage: 'Case coverage',
      availability: 'Availability',
      stationBreadth: 'Station breadth',
      continuity: 'Continuity',
      caseCoverageDescription: 'Percentage of cases with at least one measurement',
      availabilityDescription: 'Available for {{years}} years (max. 10 years = 100%)',
      stationBreadthDescription: 'Available in {{count}} station groups (6+ = 100%)',
      continuityDescription: 'Percentage of cases with ≥10 measurements (continuous capture)',
    },
  },
  pt: {
    heroTitle: 'Disponibilidade e uso',
    cases: 'Casos',
    coverage: 'Cobertura',
    stationGroups: 'Grupos de estação',
    availableSince: 'Disponível desde',
    qualityProfile: 'Perfil de qualidade',
    quality: 'Qualidade',
    activeFilter: 'Filtro ativo:',
    followingViewsFiltered: 'Os Parâmetros semelhantes estão filtrados para esta área',
    filterEnabled: 'Filtro ativado!',
    filterEnabledBody:
      'A secção Parâmetros semelhantes agora mostra dados apenas para {{group}}. Clique no ícone × no chip do filtro para voltar à visão geral.',
    availabilityByArea: 'Filtro de dados: disponibilidade por área',
    privacyTooltip: 'Estações individuais só são mostradas quando existem pelo menos 30 casos (proteção de dados)',
    selectAreaPrompt:
      'Selecione uma área para filtrar os parâmetros semelhantes para essa área.',
    area: 'Área',
    stations: 'Estações',
    details: 'Detalhes',
    allAreas: 'Todas as áreas',
    noStationsMeetMinimum: 'Nenhuma estação cumpre o mínimo de 30 casos (proteção de dados)',
    stationsInGroup: 'Estações em {{group}}',
    usageCharacteristics: 'Características de uso',
    basedOnAll: 'Os dados são baseados em todas as áreas (agregado)',
    measurementFrequency: 'Frequência de medição',
    mostCommon: 'Mais comum',
    median: 'Mediana',
    spread: 'Dispersão',
    noData: 'Nenhum dado disponível',
    measurementsPerCase: 'Medições por caso',
    statsBasedOnCases: 'Estatísticas baseadas em {{count}} casos com pelo menos uma medição',
    averageShort: 'Média',
    total: 'Total',
    continuousInfo: '{{percent}}% dos casos têm ≥10 medições (contínuas)',
    methodology:
      'Metodologia: Todas as estatísticas são baseadas em casos com pelo menos uma medição ({{count}} casos). As estatísticas de frequência de medição referem-se a {{group}}.',
    mainUsageArea: 'as principais áreas de uso',
    dataQuality: 'Qualidade dos dados e validação',
    unitsInData: 'Unidades nos dados:',
    outlierHandling: 'Tratamento de outliers',
    important: 'Importante:',
    similarParameters: 'Parâmetros com recolha semelhante',
    similarParametersTooltip: 'Parâmetros com frequência de medição, uso por estação e padrão de recolha semelhantes',
    similarParametersSummary:
      'Estes parâmetros são recolhidos com frequência semelhante e em estações semelhantes (independentemente do significado médico)',
    noSimilarParameters: 'Ainda não existem parâmetros semelhantes guardados para este filtro.',
    similarityReasons: 'Motivos de semelhança',
    frequency: 'Frequência',
    similarity: 'Semelhança',
    similarityNote:
      'Nota: a semelhança refere-se às características de recolha (frequência de medição, uso por estação, padrão de recolha), não ao significado médico.',
    similarFrequencyLabels: {
      hourly: 'De hora em hora',
      continuous: 'Contínua',
      fourHourly: 'A cada 4 horas',
      twelveToTwentyFour: '12-24h',
      irregular: 'Irregular',
      continuousBeatmet: 'Contínua (ventilados)',
      daily: 'Diária',
      everyTwoToThreeDays: 'A cada 2-3 dias',
      everyFourToSixHours: 'A cada 4-6 horas',
      asNeeded: 'Conforme necessário',
      unknown: 'Desconhecido',
    },
    measurementPatternInterpretations: {
      noUsageData:
        'Parâmetro adicionado recentemente; ainda não existem dados de utilização.',
    },
    similarityReasonLabels: {
      sameMeasurementFrequency: 'Mesma frequência de medição',
      similarStationUse: 'Uso semelhante por estação',
      continuousCapture: 'Recolha contínua',
      vitalSign: 'Sinal vital',
      relatedMeasurement: 'Medição relacionada',
      regularCapture: 'Recolha regular',
      identicalStationUse: 'Uso idêntico por estação',
      identicalIcuStations: 'Estações ICU idênticas',
      sameFrequency: 'Mesma frequência',
      identicalMonitoringSystem: 'Sistema de monitorização idêntico',
      icuVitalSign: 'Sinal vital de ICU',
      icuStandard: 'Padrão ICU',
      imcVitalSign: 'Sinal vital de IMC',
      imcStandard: 'Padrão IMC',
      identicalImcStations: 'Estações IMC idênticas',
      basicVitalSign: 'Sinal vital básico',
      stationBreadth: 'Amplitude por estação',
      generalWardRoutine: 'Rotina de enfermaria',
      intraOpStandard: 'Padrão intraoperatório',
      identicalOperatingRooms: 'Blocos operatórios idênticos',
      anesthesiaRequired: 'Obrigatório em anestesia',
      labValue: 'Valor laboratorial',
      similarSamplingFrequency: 'Frequência de colheita semelhante',
      electrolytePanel: 'Painel de eletrólitos',
    },
    stationGroupLabels: {
      icu: 'Unidades de cuidados intensivos',
      imc: 'IMC (Cuidados Intermédios)',
      ward: 'Enfermarias',
      opDiagnostics: 'Bloco operatório e diagnóstico',
      lab: 'Laboratório',
      other: 'Outros',
    },
    radar: {
      caseCoverage: 'Cobertura de casos',
      availability: 'Disponibilidade',
      stationBreadth: 'Amplitude por estação',
      continuity: 'Continuidade',
      caseCoverageDescription: 'Percentagem de casos com pelo menos uma medição',
      availabilityDescription: 'Disponível há {{years}} anos (máx. 10 anos = 100%)',
      stationBreadthDescription: 'Disponível em {{count}} grupos de estação (6+ = 100%)',
      continuityDescription: 'Percentagem de casos com ≥10 medições (recolha contínua)',
    },
  },
};

/**
 * DataExplorationDetail Component (Redesigned)
 * User-focused data exploration section with clear methodology
 *
 * @param {object} props
 * @param {object} props.data - Frequency and usage data
 * @param {string} props.sensitivity - Sensitivity level
 * @param {string} props.usagePattern - Usage pattern description
 * @param {string} props.parameterName - Parameter name for context
 */
const DataExplorationDetail = ({ data, sensitivity, usagePattern, parameterName = 'Parameter' }) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const language = i18n.resolvedLanguage?.startsWith('pt')
    ? 'pt'
    : i18n.resolvedLanguage?.startsWith('en')
      ? 'en'
      : 'de';
  const copy = DATA_EXPLORATION_COPY[language];
  const locale = language === 'pt' ? 'pt-PT' : language === 'en' ? 'en-US' : 'de-DE';

  const {
    suppressed = false,
    suppressionThreshold = null,
    caseCoverage = 0,
    totalCases = 0,
    casesWithParameter = 0,
    stationCoverage = [],
    avgMeasurementsPerCase = 0,
    medianMeasurementsPerCase = 0,
    totalMeasurements = 0,
    measurementPattern: measurementPatternRaw = {},
    continuousCases = 0,
    singleMeasurementCases = 0,
    availableSince = 'N/A',
    similarParameters = [],
    units = [],
    extractedAt = null,
  } = data;
  // #102, Rueckmeldung Runde 2 (Blocker A): `caseCoverage`/`stationCoverage`/
  // `measurementPattern` usw. sind bei einem unterdrueckten Bundle nicht
  // einfach 0, sondern fehlen -- die Destrukturierung oben faengt das mit
  // ihren Defaults ab, und genau diese Defaults sahen bislang wie echte
  // Messwerte aus (0 %, 0 Faelle). `similarParameters` und `units` stammen aus
  // einem eigenen Teil des Parameters, nicht aus diesem Aggregat, und bleiben
  // deshalb auch bei Unterdrueckung unveraendert (Abwaegung siehe MR).
  // #102, Rueckmeldung Runde 3: der Erzeuger liefert `measurementPattern` bei
  // Unterdrueckung als `null`, nicht als `undefined` -- der Default oben (`= {}`)
  // greift nur bei `undefined` und liess `null` durch. `?? {}` faengt beides.
  const measurementPattern = measurementPatternRaw ?? {};
  const hasSuppressionThreshold = Number.isFinite(suppressionThreshold);
  const suppressedExplanation = hasSuppressionThreshold
    ? t('dataCatalog.frequency.suppressedExplanationWithThreshold', { threshold: suppressionThreshold })
    : t('dataCatalog.frequency.suppressedExplanation');

  const renderSuppressedNotice = () => (
    <Box sx={{ p: 2, backgroundColor: '#fafafa', borderRadius: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <VisibilityOffIcon sx={{ color: '#616161' }} />
        <Typography variant="subtitle2" fontWeight="bold" color="textSecondary">
          {t('dataCatalog.frequency.suppressed')}
        </Typography>
      </Box>
      <Typography variant="body2" color="textSecondary">
        {suppressedExplanation}
      </Typography>
    </Box>
  );
  // Standard-Einheit: die mit dem groessten Anteil, nie die nicht erfasste
  // (`unit: null`) -- dieselbe Regel wie in ExpandedRow.js (#264).
  const standardUnitIndex = units.findIndex((u) => u.unit != null);

  // State for expandable sections
  const [expandedGroups, setExpandedGroups] = useState({});

  // State for selected station group (for filtering usage characteristics)
  const [selectedStationGroup, setSelectedStationGroup] = useState(null);

  // State for similar parameters table sorting
  const [sortBy, setSortBy] = useState('similarityScore');
  const [sortOrder, setSortOrder] = useState('desc');

  // State for showing filter alert (one-time after first selection)
  const [showFilterAlert, setShowFilterAlert] = useState(false);

  // Fehlende `percentage` einer Station ist `null` (catalogService.js,
  // normalizeStationCoverageEntry), keine 0 -- `0 + null` waere aber eine
  // stille echte Zahl statt "keine Angabe" (Rueckmeldung Runde 4, NEU B1).
  const hasNumber = (value) => typeof value === 'number' && Number.isFinite(value);
  // Rueckmeldung Runde 5, NEU B1: `Number(value || 0)` machte aus einem
  // fehlenden (`null`/`undefined`) Einzelwert eine stille echte 0 --
  // dieselbe Faelle-Zahl wie bei einem tatsaechlich leeren Datensatz (AK3).
  // Gleiche Konvention wie FrequencyMetrics.js: '–' statt 0 bei fehlendem Wert.
  const formatNumber = (value) => (hasNumber(value) ? value.toLocaleString(locale) : '–');
  // #266: Ersetzt den beim Import auf deutsch eingefrorenen Satz
  // ("Profiling vom <ISO-Zeitstempel>: ... Aggregate unter ... sind
  // unterdrueckt, nicht gerundet.") durch dieselben Bausteine als Felder --
  // das Datum in der Schreibweise der gewaehlten Sprache, wie bei #243.
  const hasProfilingSummary =
    Boolean(extractedAt) &&
    hasNumber(totalMeasurements) &&
    hasNumber(casesWithParameter) &&
    hasNumber(totalCases);
  const profilingSummaryText = hasProfilingSummary
    ? [
        t('dataCatalog.frequency.profilingSummary', {
          date: new Date(extractedAt).toLocaleDateString(locale),
          totalMeasurements: formatNumber(totalMeasurements),
          casesWithParameter: formatNumber(casesWithParameter),
          totalCases: formatNumber(totalCases),
        }),
        hasSuppressionThreshold
          ? t('dataCatalog.frequency.profilingSuppressionNote', { threshold: suppressionThreshold })
          : null,
      ].filter(Boolean).join(' ')
    : '';
  const normalizedSimilarParameters = Array.isArray(similarParameters)
    ? similarParameters
      .filter(item => item && typeof item === 'object')
      .map(item => ({
        id: item.id,
        name: item.name || item.localName || 'Unknown',
        description: item.description || '',
        caseCoverage: Number(item.caseCoverage || 0),
        frequency: item.frequency || copy.similarFrequencyLabels.unknown,
        stationCount: Number(item.stationCount || 0),
        similarityScore: Number(item.similarityScore || 0),
        similarityReasons: Array.isArray(item.similarityReasons)
          ? item.similarityReasons
          : (Array.isArray(item.reasons) ? item.reasons : []),
        stationGroup: item.stationGroup || '',
      }))
    : [];

  const stationGroupKeyMap = {
    'Intensivstationen': 'icu',
    'IMC (Intermediate Care)': 'imc',
    'Normalstationen': 'ward',
    'OP & Diagnostik': 'opDiagnostics',
    'Labor': 'lab',
    'Sonstige': 'other',
  };

  const translateStationGroupLabel = (name) => {
    const key = stationGroupKeyMap[name];
    return key ? copy.stationGroupLabels[key] : name;
  };

  const translateSimilarFrequency = (label) => {
    const mapping = {
      'Stündlich': copy.similarFrequencyLabels.hourly,
      'Kontinuierlich': copy.similarFrequencyLabels.continuous,
      '4-stündlich': copy.similarFrequencyLabels.fourHourly,
      '12-24h': copy.similarFrequencyLabels.twelveToTwentyFour,
      'Unregelmäßig': copy.similarFrequencyLabels.irregular,
      'Kontinuierlich (beatmet)': copy.similarFrequencyLabels.continuousBeatmet,
      'Täglich': copy.similarFrequencyLabels.daily,
      'Alle 2-3 Tage': copy.similarFrequencyLabels.everyTwoToThreeDays,
      '4-6 Stunden': copy.similarFrequencyLabels.everyFourToSixHours,
      'Bei Bedarf': copy.similarFrequencyLabels.asNeeded,
      'Unbekannt': copy.similarFrequencyLabels.unknown,
    };

    return mapping[label] || label;
  };

  // **Keine klinischen Deutungen mehr (#267).** Hier stand eine Tabelle, die
  // erfundene Einordnungen wie "Wichtiger Sepsis- und Schock-Marker" an
  // Parameter haengte. Ueber 100 Detailkarten von hospital1 geprueft: die API
  // liefert nichts dergleichen -- kein einziges "Sepsis". Was ankommt, ist die
  // erzeugte Zeile ueber Messungen und Aufnahmen, und die bleibt -- seit #266
  // als `profilingSummaryText` aus Feldern statt als roher API-Satz.
  //
  // Eine klinische Einordnung ist kein Messergebnis: sie gilt fuer einen
  // Anwendungsfall und nicht fuer den naechsten -- derselbe Haemoglobinwert ist
  // in einer Anaemie-Studie etwas anderes als in einer Sepsis-Kohorte. Im
  // Katalog stand sie ohne Autor, ohne Datum und ohne Quelle neben echten
  // Messzahlen und war von ihnen nicht zu unterscheiden.

  const translateSimilarityReason = (reason) => {
    const mapping = {
      'Gleiche Messfrequenz': copy.similarityReasonLabels.sameMeasurementFrequency,
      'Ähnliche Stationsnutzung': copy.similarityReasonLabels.similarStationUse,
      'Kontinuierliche Erfassung': copy.similarityReasonLabels.continuousCapture,
      'Vitalparameter': copy.similarityReasonLabels.vitalSign,
      'Zusammengehörige Messung': copy.similarityReasonLabels.relatedMeasurement,
      'Regelmäßige Erfassung': copy.similarityReasonLabels.regularCapture,
      'Identische Stationsnutzung': copy.similarityReasonLabels.identicalStationUse,
      'Identische ICU-Stationen': copy.similarityReasonLabels.identicalIcuStations,
      'Gleiche Frequenz': copy.similarityReasonLabels.sameFrequency,
      'Identisches Monitoring-System': copy.similarityReasonLabels.identicalMonitoringSystem,
      'ICU-Vitalparameter': copy.similarityReasonLabels.icuVitalSign,
      'ICU-Standard': copy.similarityReasonLabels.icuStandard,
      'IMC-Vitalparameter': copy.similarityReasonLabels.imcVitalSign,
      'IMC-Standard': copy.similarityReasonLabels.imcStandard,
      'Identische IMC-Stationen': copy.similarityReasonLabels.identicalImcStations,
      'Basisvitalparameter': copy.similarityReasonLabels.basicVitalSign,
      'Stationsbreite': copy.similarityReasonLabels.stationBreadth,
      'Normalstations-Routine': copy.similarityReasonLabels.generalWardRoutine,
      'Intraop-Standard': copy.similarityReasonLabels.intraOpStandard,
      'Identische OP-Säle': copy.similarityReasonLabels.identicalOperatingRooms,
      'Anästhesie-Pflicht': copy.similarityReasonLabels.anesthesiaRequired,
      'Laborwert': copy.similarityReasonLabels.labValue,
      'Ähnliche Abnahmefrequenz': copy.similarityReasonLabels.similarSamplingFrequency,
      'Elektrolyt-Panel': copy.similarityReasonLabels.electrolytePanel,
      'Ähnliche Messfrequenz': copy.similarityReasonLabels.sameMeasurementFrequency,
    };

    return mapping[reason] || reason;
  };

  // Group stations by category (based on naming convention)
  const groupStationsByType = (stations) => {
    const groups = {
      'Intensivstationen': [],
      'IMC (Intermediate Care)': [],
      'Normalstationen': [],
      'OP & Diagnostik': [],
      'Labor': [],
      'Sonstige': []
    };

    stations.forEach(station => {
      if (station.name.includes('ICU') || station.name.includes('NICU') || station.name.includes('PICU')) {
        groups['Intensivstationen'].push(station);
      } else if (station.name.includes('IMC')) {
        groups['IMC (Intermediate Care)'].push(station);
      } else if (station.name.includes('Station') || station.name.includes('Stroke Unit')) {
        groups['Normalstationen'].push(station);
      } else if (station.name.includes('OP') || station.name.includes('Radiologie') || station.name.includes('Endoskopie') || station.name.includes('Notaufnahme')) {
        groups['OP & Diagnostik'].push(station);
      } else if (station.name.includes('Labor') || station.name.includes('Blutbank')) {
        groups['Labor'].push(station);
      } else {
        groups['Sonstige'].push(station);
      }
    });

    // Remove empty groups
    return Object.entries(groups)
      .filter(([_, stations]) => stations.length > 0)
      .map(([groupName, stations]) => {
        // Calculate group statistics
        // Rueckmeldung Runde 4, NEU B2: `(s.caseCount || 0)` zaehlte eine
        // Station ohne `caseCount` als 0 mit -- fehlt allen Stationen der
        // Gruppe die Fallzahl (der von :646 selbst behandelte Fall), summierte
        // das zu einer erfundenen 0 statt "keine Angabe" (AK3). Analog zu
        // `stationsWithPercentage`/`avgGroupCoverage` direkt darunter: nur
        // Stationen mit tatsaechlicher Fallzahl zaehlen, keine zaehlt zu null.
        const stationsWithCaseCount = stations.filter(s => hasNumber(s.caseCount));
        const totalGroupCases =
          stationsWithCaseCount.length > 0
            ? stationsWithCaseCount.reduce((sum, s) => sum + s.caseCount, 0)
            : null;
        const stationsWithPercentage = stations.filter(s => hasNumber(s.percentage));
        const avgGroupCoverage =
          stationsWithPercentage.length > 0
            ? stationsWithPercentage.reduce((sum, s) => sum + s.percentage, 0) / stationsWithPercentage.length
            : null;

        return {
          name: groupName,
          // Only apply privacy threshold when caseCount is explicitly provided; show all stations when caseCount is absent
          stations: stations.filter(s => s.caseCount == null || (s.caseCount || 0) >= 30),
          totalCases: totalGroupCases,
          avgCoverage: avgGroupCoverage,
          stationCount: stations.length
        };
      });
  };

  const stationGroups = groupStationsByType(stationCoverage);

  const toggleGroup = (groupName) => {
    setExpandedGroups(prev => ({
      ...prev,
      [groupName]: !prev[groupName]
    }));
  };

  const handleStationGroupChange = (groupName) => {
    setSelectedStationGroup(groupName);

    // Show filter alert on first selection
    if (groupName && !showFilterAlert) {
      setShowFilterAlert(true);
    }
  };

  // Calculate continuous case percentage
  // Rueckmeldung Runde 5, NEU B1: `casesWithParameter > 0` ist bei `null`
  // (fehlende Angabe) ebenso `false` wie bei einer echten 0 -- ohne
  // `hasNumber` liest sich "keine Angabe" als 0 % kontinuierliche Faelle (AK3).
  const hasContinuousCasePercentage =
    hasNumber(continuousCases) && hasNumber(casesWithParameter) && casesWithParameter > 0;
  const continuousCasePercentage = hasContinuousCasePercentage
    ? ((continuousCases / casesWithParameter) * 100).toFixed(0)
    : null;

  // Prepare data for radar chart (parameter quality profile)
  const getQualityProfileData = () => {
    // Calculate scores (0-100) for different quality dimensions
    const yearsAvailable = availableSince ? new Date().getFullYear() - parseInt(availableSince.split('-')[0]) : 0;
    const timeScore = Math.min(100, (yearsAvailable / 10) * 100); // 10 years = 100%

    return [
      {
        dimension: copy.radar.caseCoverage,
        // Qualitaets-Score (0-100), keine Einzelmetrik im Sinn von AK3 --
        // eine fehlende Angabe zaehlt hier bewusst als 0 Punkte, wie die
        // anderen Radar-Dimensionen (z. B. `timeScore` oben) es auch tun.
        value: hasNumber(caseCoverage) ? caseCoverage : 0,
        fullMark: 100,
        description: copy.radar.caseCoverageDescription
      },
      {
        dimension: copy.radar.availability,
        value: timeScore,
        fullMark: 100,
        description: copy.radar.availabilityDescription.replace('{{years}}', yearsAvailable)
      },
      {
        dimension: copy.radar.stationBreadth,
        value: Math.min(100, (stationGroups.length / 6) * 100), // 6+ groups = 100%
        fullMark: 100,
        description: copy.radar.stationBreadthDescription.replace('{{count}}', stationGroups.length)
      },
      {
        dimension: copy.radar.continuity,
        value: hasContinuousCasePercentage ? parseInt(continuousCasePercentage) : 0,
        fullMark: 100,
        description: copy.radar.continuityDescription
      },
    ];
  };

  // Custom tooltip for radar chart
  const CustomRadarTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <Paper sx={{ p: 1.5, maxWidth: 250 }}>
          <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
            {data.dimension}
          </Typography>
          <Typography variant="body2" color="primary" fontWeight="bold" sx={{ mb: 0.5 }}>
            {Math.round(data.value)}%
          </Typography>
          <Typography variant="caption" color="textSecondary">
            {data.description}
          </Typography>
        </Paper>
      );
    }
    return null;
  };

  // Handle sort for similar parameters table
  const handleSortRequest = (property) => {
    const isAsc = sortBy === property && sortOrder === 'asc';
    setSortOrder(isAsc ? 'desc' : 'asc');
    setSortBy(property);
  };

  // Sort similar parameters
  const sortSimilarParameters = (parameters) => {
    return [...parameters].sort((a, b) => {
      let aValue = a[sortBy];
      let bValue = b[sortBy];

      // Handle string comparison
      if (typeof aValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }

      if (sortOrder === 'asc') {
        return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
      } else {
        return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
      }
    });
  };

  // Get filtered similar parameters based on selected station group
  const getFilteredSimilarParameters = (selectedGroup) => {
    return selectedGroup
      ? normalizedSimilarParameters.filter(
          item => !item.stationGroup || item.stationGroup === selectedGroup
        )
      : normalizedSimilarParameters;
  };

  const filteredSimilarParameters = sortSimilarParameters(
    getFilteredSimilarParameters(selectedStationGroup)
  );

  return (
    <Box>
      {/* Hero Section: Key Metrics */}
      <Paper
        elevation={0}
        sx={{
          px: 3,
          py: 1,
          mb: 3,
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white'
        }}
      >
        <Typography variant="h5" fontWeight="bold" gutterBottom>
          {copy.heroTitle}
        </Typography>

        {suppressed ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 2 }}>
            <VisibilityOffIcon />
            <Box>
              <Typography variant="subtitle1" fontWeight="bold">
                {t('dataCatalog.frequency.suppressed')}
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                {suppressedExplanation}
              </Typography>
            </Box>
          </Box>
        ) : (
        <Grid container spacing={2} sx={{ mt: 0.5, alignItems: 'center' }}>
          {/* Left side: Metrics in 2x2 Grid */}
          <Grid item xs={12} md={6}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, py: 0.5 }}>
              {/* First Row */}
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Chip
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                      <Typography variant="body2" sx={{ opacity: 0.85 }}>
                        {copy.cases}
                      </Typography>
                      <Typography variant="h6" fontWeight="bold">
                        {formatNumber(casesWithParameter)}
                      </Typography>
                    </Box>
                  }
                  sx={{
                    backgroundColor: 'rgba(255,255,255,0.2)',
                    color: 'white',
                    height: 'auto',
                    px: 2,
                    py: 0.75,
                    flex: 1,
                    justifyContent: 'space-between',
                    '& .MuiChip-label': {
                      width: '100%',
                      px: 0
                    }
                  }}
                />
                <Chip
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                      <Typography variant="body2" sx={{ opacity: 0.85 }}>
                        {copy.coverage}
                      </Typography>
                      <Typography variant="h6" fontWeight="bold">
                        {hasNumber(caseCoverage) ? `${caseCoverage}%` : '–'}
                      </Typography>
                    </Box>
                  }
                  sx={{
                    backgroundColor: 'rgba(255,255,255,0.2)',
                    color: 'white',
                    height: 'auto',
                    px: 2,
                    py: 0.75,
                    flex: 1,
                    justifyContent: 'space-between',
                    '& .MuiChip-label': {
                      width: '100%',
                      px: 0
                    }
                  }}
                />
              </Box>

              {/* Second Row */}
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Chip
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                      <Typography variant="body2" sx={{ opacity: 0.85 }}>
                        {copy.stationGroups}
                      </Typography>
                      <Typography variant="h6" fontWeight="bold">
                        {stationGroups.length}
                      </Typography>
                    </Box>
                  }
                  sx={{
                    backgroundColor: 'rgba(255,255,255,0.2)',
                    color: 'white',
                    height: 'auto',
                    px: 2,
                    py: 0.75,
                    flex: 1,
                    justifyContent: 'space-between',
                    '& .MuiChip-label': {
                      width: '100%',
                      px: 0
                    }
                  }}
                />
                <Chip
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                      <Typography variant="body2" sx={{ opacity: 0.85 }}>
                        {copy.availableSince}
                      </Typography>
                      <Typography variant="h6" fontWeight="bold">
                        {availableSince}
                      </Typography>
                    </Box>
                  }
                  sx={{
                    backgroundColor: 'rgba(255,255,255,0.2)',
                    color: 'white',
                    height: 'auto',
                    px: 2,
                    py: 0.75,
                    flex: 1,
                    justifyContent: 'space-between',
                    '& .MuiChip-label': {
                      width: '100%',
                      px: 0
                    }
                  }}
                />
              </Box>
            </Box>
          </Grid>

          {/* Right side: Radar Chart */}
          <Grid item xs={12} md={6}>
            <Box sx={{
              height: { xs: 220, md: 250 },
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <Typography variant="subtitle2" sx={{ opacity: 0.95, mb: 1, fontWeight: 600 }}>
                {copy.qualityProfile}
              </Typography>
              <Box sx={{ width: '100%', height: '100%' }}>
                <ResponsiveContainer width="100%" height="100%">
                  <RadarChart data={getQualityProfileData()} outerRadius="65%">
                    <PolarGrid stroke="rgba(255,255,255,0.3)" />
                    <PolarAngleAxis
                      dataKey="dimension"
                      tick={{ fill: 'white', fontSize: 12 }}
                    />
                    <PolarRadiusAxis
                      angle={90}
                      domain={[0, 100]}
                      tick={{ fill: 'white', fontSize: 9 }}
                      tickCount={6}
                    />
                    <RechartsTooltip content={<CustomRadarTooltip />} />
                    <Radar
                      name={copy.quality}
                      dataKey="value"
                      stroke="#ffffff"
                      strokeWidth={2.5}
                      fill="rgba(255,255,255,0.6)"
                      fillOpacity={0.5}
                    />
                  </RadarChart>
                </ResponsiveContainer>
              </Box>
            </Box>
          </Grid>
        </Grid>
        )}
      </Paper>

      {/* Sticky Filter Bar (when filter is active) */}
      {selectedStationGroup && (
        <Paper
          elevation={3}
          sx={{
            position: 'sticky',
            top: 64, // Adjust based on your header height
            zIndex: 100,
            p: 1.5,
            mb: 2,
            backgroundColor: '#e3f2fd',
            borderLeft: '4px solid #1976d2',
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            flexWrap: 'wrap'
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <FilterAltIcon color="primary" />
            <Typography variant="body2" fontWeight="bold" color="primary">
              {copy.activeFilter}
            </Typography>
            <Chip
              label={translateStationGroupLabel(selectedStationGroup)}
              color="primary"
              onDelete={() => handleStationGroupChange(null)}
              sx={{ fontWeight: 'bold' }}
            />
          </Box>
          <Typography variant="caption" color="textSecondary">
            {copy.followingViewsFiltered}
          </Typography>
        </Paper>
      )}

      {/* Einmal-Alert nach erster Auswahl */}
      {showFilterAlert && selectedStationGroup && (
        <Fade in={showFilterAlert} timeout={500}>
          <Alert
            severity="info"
            onClose={() => setShowFilterAlert(false)}
            sx={{ mb: 2 }}
          >
            <Typography variant="body2" fontWeight="bold" gutterBottom>
              {copy.filterEnabled}
            </Typography>
            <Typography variant="caption">
              {copy.filterEnabledBody.replace('{{group}}', translateStationGroupLabel(selectedStationGroup))}
            </Typography>
          </Alert>
        </Fade>
      )}

      {/* Section 1: Verfügbarkeit nach Bereich */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <TuneIcon color="primary" sx={{ fontSize: 28 }} />
          <Typography variant="h6" fontWeight="bold">
            {copy.availabilityByArea}
          </Typography>
          <Tooltip title={copy.privacyTooltip}>
            <IconButton size="small">
              <InfoOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>

        {suppressed ? renderSuppressedNotice() : (
        <>
        <Alert severity="info" icon={<TuneIcon />} sx={{ mb: 2 }}>
          <Typography variant="body2">
            <strong>{copy.selectAreaPrompt}</strong>
          </Typography>
        </Alert>

        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox" width="50px"></TableCell>
                <TableCell>{copy.area}</TableCell>
                <TableCell align="center">{copy.stations}</TableCell>
                <TableCell align="center">{copy.cases}</TableCell>
                <TableCell>{copy.coverage}</TableCell>
                <TableCell align="center" width="60px">{copy.details}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {/* "Alle Bereiche" row */}
              <TableRow
                hover
                sx={{
                  cursor: 'pointer',
                  backgroundColor: selectedStationGroup === null ? '#e3f2fd' : 'inherit',
                  '&:hover': {
                    backgroundColor: selectedStationGroup === null ? '#e3f2fd !important' : undefined
                  }
                }}
                onClick={() => handleStationGroupChange(null)}
              >
                <TableCell padding="checkbox">
                  <Radio
                    checked={selectedStationGroup === null}
                    onChange={() => handleStationGroupChange(null)}
                    value="all"
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  <Typography variant="body2" fontWeight="bold">
                    {copy.allAreas}
                  </Typography>
                </TableCell>
                <TableCell align="center">
                  <Chip
                    label={stationGroups.reduce((sum, g) => sum + g.stationCount, 0)}
                    size="small"
                    variant="outlined"
                  />
                </TableCell>
                <TableCell align="center">
                  <Typography variant="body2">
                    {formatNumber(
                      hasNumber(casesWithParameter)
                        ? casesWithParameter
                        : (stationGroups.some(g => hasNumber(g.totalCases))
                          ? stationGroups.reduce((sum, g) => sum + (g.totalCases || 0), 0)
                          : null)
                    )}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <LinearProgress
                      variant="determinate"
                      value={hasNumber(caseCoverage) ? caseCoverage : 0}
                      sx={{
                        flex: 1,
                        height: 8,
                        borderRadius: 1,
                        backgroundColor: '#e0e0e0',
                        '& .MuiLinearProgress-bar': {
                          backgroundColor: hasNumber(caseCoverage)
                            ? (caseCoverage >= 80 ? '#4caf50' : caseCoverage >= 50 ? '#ff9800' : '#f44336')
                            : '#e0e0e0'
                        }
                      }}
                    />
                    <Typography variant="body2" sx={{ minWidth: 45 }}>
                      {hasNumber(caseCoverage) ? `${Math.round(caseCoverage)}%` : '–'}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell align="center">
                  <Typography variant="caption" color="textSecondary">-</Typography>
                </TableCell>
              </TableRow>

              {/* Station groups */}
              {stationGroups.map((group, idx) => (
                <React.Fragment key={idx}>
                  <TableRow
                    hover
                    sx={{
                      cursor: 'pointer',
                      backgroundColor: selectedStationGroup === group.name ? '#e3f2fd' : 'inherit',
                      '&:hover': {
                        backgroundColor: selectedStationGroup === group.name ? '#e3f2fd !important' : undefined
                      }
                    }}
                    onClick={() => handleStationGroupChange(group.name)}
                  >
                    <TableCell padding="checkbox">
                      <Radio
                        checked={selectedStationGroup === group.name}
                        onChange={() => handleStationGroupChange(group.name)}
                        value={group.name}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">
                        {translateStationGroupLabel(group.name)}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={group.stationCount}
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="body2">
                        {formatNumber(group.totalCases)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <LinearProgress
                          variant="determinate"
                          value={hasNumber(group.avgCoverage) ? group.avgCoverage : 0}
                          sx={{
                            flex: 1,
                            height: 8,
                            borderRadius: 1,
                            backgroundColor: '#e0e0e0',
                            '& .MuiLinearProgress-bar': {
                              backgroundColor: hasNumber(group.avgCoverage)
                                ? (group.avgCoverage >= 80 ? '#4caf50' : group.avgCoverage >= 50 ? '#ff9800' : '#f44336')
                                : '#e0e0e0'
                            }
                          }}
                        />
                        <Typography variant="body2" sx={{ minWidth: 45 }}>
                          {hasNumber(group.avgCoverage) ? `${Math.round(group.avgCoverage)}%` : '–'}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell align="center">
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleGroup(group.name);
                        }}
                      >
                        {expandedGroups[group.name] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                      </IconButton>
                    </TableCell>
                  </TableRow>

                  {/* Expandable station details row */}
                  <TableRow>
                    <TableCell colSpan={6} sx={{ p: 0, borderBottom: expandedGroups[group.name] ? undefined : 'none' }}>
                      <Collapse in={expandedGroups[group.name]}>
                        <Box sx={{ p: 2, backgroundColor: '#f9f9f9' }}>
                          {group.stations.length > 0 ? (
                            <Box>
                              <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                                {copy.stationsInGroup.replace('{{group}}', translateStationGroupLabel(group.name))}
                              </Typography>
                              {group.stations.map((station, sIdx) => (
                                <Box key={sIdx} sx={{ mb: 1.5 }}>
                                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                                    <Typography variant="body2">
                                      {station.name}
                                    </Typography>
                                    <Chip
                                      label={hasNumber(station.percentage) ? `${station.percentage}%` : '–'}
                                      size="small"
                                      sx={{
                                        backgroundColor: station.color || '#1976d2',
                                        color: 'white',
                                        fontSize: '0.7rem',
                                        height: 20
                                      }}
                                    />
                                  </Box>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                    <LinearProgress
                                      variant="determinate"
                                      value={hasNumber(station.percentage) ? station.percentage : 0}
                                      sx={{
                                        flex: 1,
                                        height: 6,
                                        borderRadius: 1,
                                        '& .MuiLinearProgress-bar': {
                                          backgroundColor: station.color || '#1976d2'
                                        }
                                      }}
                                    />
                                    {station.caseCount && (
                                      <Typography variant="caption" color="textSecondary" sx={{ minWidth: 80 }}>
                                      {formatNumber(station.caseCount)} {copy.cases}
                                      </Typography>
                                    )}
                                  </Box>
                                </Box>
                              ))}
                            </Box>
                          ) : (
                            <Alert severity="info" sx={{ fontSize: '0.85rem' }}>
                              {copy.noStationsMeetMinimum}
                            </Alert>
                          )}
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        </>
        )}
      </Paper>

      {/* Section 2: Nutzungs-Charakteristik */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, flexWrap: 'wrap' }}>
          <BarChartIcon color="primary" />
          <Typography variant="h6" fontWeight="bold">
            {copy.usageCharacteristics}
          </Typography>
        </Box>

        {suppressed ? renderSuppressedNotice() : (
        <>
        <Typography variant="body2" color="textSecondary" paragraph>
          {copy.basedOnAll}
        </Typography>

        <Grid container spacing={3}>
          {/* Messfrequenz */}
          <Grid item xs={12} md={6}>
            <Box sx={{ p: 2, backgroundColor: '#f5f5f5', borderRadius: 1, height: '100%' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <TimelineIcon fontSize="small" color="primary" />
                <Typography variant="subtitle2" fontWeight="bold">
                  {copy.measurementFrequency}
                </Typography>
              </Box>

              {measurementPattern.typical ? (
                <Box>
                  {/* Key Stats in compact cards */}
                  <Grid container spacing={1} sx={{ mb: 2 }}>
                    <Grid item xs={4}>
                      <Box sx={{ p: 1, backgroundColor: 'white', borderRadius: 1, textAlign: 'center' }}>
                        <Typography variant="caption" color="textSecondary" display="block">
                          {copy.mostCommon}
                        </Typography>
                        <Typography variant="body1" fontWeight="bold">
                          {translateSimilarFrequency(measurementPattern.typical)}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={4}>
                      <Box sx={{ p: 1, backgroundColor: 'white', borderRadius: 1, textAlign: 'center' }}>
                        <Typography variant="caption" color="textSecondary" display="block">
                          {copy.median}
                        </Typography>
                        <Typography variant="body1" fontWeight="bold">
                          {measurementPattern.avgInterval || 'N/A'}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={4}>
                      <Box sx={{ p: 1, backgroundColor: 'white', borderRadius: 1, textAlign: 'center' }}>
                        <Typography variant="caption" color="textSecondary" display="block">
                          {copy.spread}
                        </Typography>
                        <Typography variant="body1" fontWeight="bold">
                          {measurementPattern.stdDev || 'N/A'}
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>

                  {hasProfilingSummary && (
                    <Alert severity="info" sx={{ mt: 2, fontSize: '0.85rem' }}>
                      {profilingSummaryText}
                    </Alert>
                  )}
                </Box>
              ) : (
                <Typography variant="body2" color="textSecondary">
                  {copy.noData}
                </Typography>
              )}
            </Box>
          </Grid>

          {/* Messungen pro Fall */}
          <Grid item xs={12} md={6}>
            <Box sx={{ p: 2, backgroundColor: '#f5f5f5', borderRadius: 1, height: '100%' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <BarChartIcon fontSize="small" color="primary" />
                <Typography variant="subtitle2" fontWeight="bold">
                  {copy.measurementsPerCase}
                </Typography>
                <Tooltip title={copy.statsBasedOnCases.replace('{{count}}', formatNumber(casesWithParameter))}>
                  <IconButton size="small">
                    <InfoOutlinedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>

              {/* Key Stats */}
              <Grid container spacing={1} sx={{ mb: 2 }}>
                <Grid item xs={4}>
                  <Box sx={{ textAlign: 'center', p: 1, backgroundColor: 'white', borderRadius: 1 }}>
                    <Typography variant="caption" color="textSecondary">
                      {copy.median}
                    </Typography>
                    <Typography variant="h6" fontWeight="bold" color="primary">
                      {hasNumber(medianMeasurementsPerCase) ? medianMeasurementsPerCase : '–'}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={4}>
                  <Box sx={{ textAlign: 'center', p: 1, backgroundColor: 'white', borderRadius: 1 }}>
                    <Typography variant="caption" color="textSecondary">
                      {copy.averageShort}
                    </Typography>
                    <Typography variant="h6" fontWeight="bold" color="primary">
                      {hasNumber(avgMeasurementsPerCase) ? avgMeasurementsPerCase : '–'}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={4}>
                  <Box sx={{ textAlign: 'center', p: 1, backgroundColor: 'white', borderRadius: 1 }}>
                    <Typography variant="caption" color="textSecondary">
                      {copy.total}
                    </Typography>
                    <Typography variant="h6" fontWeight="bold" color="primary">
                      {hasNumber(totalMeasurements) ? `${(totalMeasurements / 1000000).toFixed(1)}M` : '–'}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>

              <Alert severity="info" sx={{ fontSize: '0.75rem', py: 0.5 }}>
                {hasContinuousCasePercentage
                  ? copy.continuousInfo.replace('{{percent}}', continuousCasePercentage)
                  : copy.noData}
              </Alert>
            </Box>
          </Grid>
        </Grid>

        <Alert severity="info" icon={<InfoOutlinedIcon />} sx={{ mt: 2 }}>
          <Typography variant="caption">
            {copy.methodology
              .replace('{{count}}', formatNumber(casesWithParameter))
              .replace('{{group}}', copy.mainUsageArea)}
          </Typography>
        </Alert>
        </>
        )}
      </Paper>

      {/* Section 3: Datenqualität */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2.5, flexWrap: 'wrap' }}>
          <CheckCircleIcon color="success" />
          <Typography variant="h6" fontWeight="bold">
            {copy.dataQuality}
          </Typography>
        </Box>

        <Box>
            {/* Einheiten - Verteilung in den Rohdaten (#263): kommt aus
                `data.units`, nicht mehr aus erfundenen Prozentzahlen. Ohne
                erfasste Einheiten wird der Block ausgeblendet, nicht mit
                Platzhaltern gefuellt. */}
            {units.length > 0 && (
              <Box sx={{ mb: 2.5, p: 1.5, backgroundColor: '#f5f5f5', borderRadius: 1, border: '1px solid #e0e0e0' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 0.5 }}>
                  <InfoOutlinedIcon fontSize="small" color="primary" />
                  <Typography variant="caption" fontWeight="bold">{copy.unitsInData}</Typography>
                  {units.map((unitData, idx) => (
                    <Chip
                      key={idx}
                      label={`${unitData.unit == null ? t('dataCatalog.expanded.unitNotRecorded') : unitData.unit}${unitData.percentage != null ? ` (${formatiereProzent(unitData.percentage)}%)` : ''}`}
                      size="small"
                      color={idx === standardUnitIndex ? 'primary' : 'default'}
                      variant={idx === standardUnitIndex ? 'filled' : 'outlined'}
                      sx={{
                        fontSize: '0.7rem',
                        fontWeight: idx === standardUnitIndex ? 'bold' : 'normal',
                        ...(idx === standardUnitIndex ? {} : { borderColor: '#1976d2', color: '#1976d2' })
                      }}
                    />
                  ))}
                </Box>
              </Box>
            )}

            {/* Ausreisser (#252, #263): derselbe Boxplot-Baustein wie im
                aufgeklappten Katalog-Bereich, aus den bereits geladenen
                Feldern von `measurementPattern` -- keine neue Abfrage. */}
            {measurementPattern.medianValue != null && (
              <Box sx={{ mb: 2.5 }}>
                <Typography variant="subtitle2" fontWeight="bold" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, fontSize: '0.875rem' }}>
                  <WarningAmberIcon fontSize="small" color="warning" />
                  {copy.outlierHandling}
                </Typography>
                {/* Der Hinweistext ist entfallen (#267): er nannte Sepsis,
                    Schock und hypertensive Krisen -- eine klinische Aussage
                    ueber Daten, die niemand geprueft hat. Was bleibt, ist der
                    Boxplot: er zeigt am Objekt, wo die Extremwerte liegen und
                    ob sie ausserhalb des Tukey-Zauns sind (#252). Eine
                    Zeichnung der eigenen Zahlen statt eines Satzes ueber
                    fremde. */}
                <Paper variant="outlined" sx={{ p: 1.5, backgroundColor: '#fffbf0' }}>
                  <BoxplotZeile muster={measurementPattern} />
                </Paper>
              </Box>
            )}
        </Box>
      </Paper>

      {/* Section 4: Parameter mit ähnlicher Erfassung */}
      <Paper
        sx={{
          p: 3,
          position: 'relative',
          ...(selectedStationGroup && {
            borderLeft: '4px solid #1976d2',
            backgroundColor: '#fafbff'
          })
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, flexWrap: 'wrap' }}>
          <SearchIcon color="primary" />
          <Typography variant="h6" fontWeight="bold">
            {copy.similarParameters}
          </Typography>
          <Tooltip title={copy.similarParametersTooltip}>
            <IconButton size="small">
              <InfoOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          {selectedStationGroup && (
            <Chip
              label={translateStationGroupLabel(selectedStationGroup)}
              color="primary"
              size="small"
              onDelete={() => handleStationGroupChange(null)}
              sx={{ ml: 'auto' }}
            />
          )}
        </Box>

        <Typography variant="body2" color="textSecondary" paragraph>
          {copy.similarParametersSummary}
        </Typography>

        <Box>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>
                      <TableSortLabel
                        active={sortBy === 'name'}
                        direction={sortBy === 'name' ? sortOrder : 'asc'}
                        onClick={() => handleSortRequest('name')}
                      >
                        Parameter
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>{copy.similarityReasons}</TableCell>
                    <TableCell align="center">
                      <TableSortLabel
                        active={sortBy === 'caseCoverage'}
                        direction={sortBy === 'caseCoverage' ? sortOrder : 'asc'}
                        onClick={() => handleSortRequest('caseCoverage')}
                      >
                        {copy.coverage}
                      </TableSortLabel>
                    </TableCell>
                    <TableCell align="center">
                      <TableSortLabel
                        active={sortBy === 'frequency'}
                        direction={sortBy === 'frequency' ? sortOrder : 'asc'}
                        onClick={() => handleSortRequest('frequency')}
                      >
                        {copy.frequency}
                      </TableSortLabel>
                    </TableCell>
                    <TableCell align="center">
                      <TableSortLabel
                        active={sortBy === 'stationCount'}
                        direction={sortBy === 'stationCount' ? sortOrder : 'asc'}
                        onClick={() => handleSortRequest('stationCount')}
                      >
                        {copy.stations}
                      </TableSortLabel>
                    </TableCell>
                    <TableCell align="center">
                      <TableSortLabel
                        active={sortBy === 'similarityScore'}
                        direction={sortBy === 'similarityScore' ? sortOrder : 'asc'}
                        onClick={() => handleSortRequest('similarityScore')}
                      >
                        {copy.similarity}
                      </TableSortLabel>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredSimilarParameters.length > 0 ? filteredSimilarParameters.map((param, idx) => (
                <TableRow
                  key={idx}
                  hover
                  sx={{
                    cursor: 'pointer',
                    '&:hover': {
                      backgroundColor: '#f5f5f5'
                    }
                  }}
                  onClick={() => navigate(`/parameter/${getParameterLookupId(param)}`)}
                >
                  <TableCell>
                    <Typography variant="body2" fontWeight="medium">
                      {param.name}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      {param.description}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                      {param.similarityReasons.map((reason, rIdx) => (
                        <Chip
                          key={rIdx}
                          label={translateSimilarityReason(reason)}
                          size="small"
                          variant="outlined"
                          color="primary"
                          sx={{ fontSize: '0.7rem', height: 20 }}
                        />
                      ))}
                    </Box>
                  </TableCell>
                  <TableCell align="center">
                    <Chip
                      label={`${param.caseCoverage}%`}
                      size="small"
                      sx={{
                        backgroundColor: '#e3f2fd',
                        fontSize: '0.75rem'
                      }}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, justifyContent: 'center' }}>
                      <TimelineIcon fontSize="small" color="action" />
                      <Typography variant="body2">
                        {translateSimilarFrequency(param.frequency)}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell align="center">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, justifyContent: 'center' }}>
                      <GroupIcon fontSize="small" color="action" />
                      <Typography variant="body2">
                        {param.stationCount}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell align="center">
                    <Box
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 50,
                        height: 50,
                        borderRadius: '50%',
                        backgroundColor: param.similarityScore >= 80 ? '#e8f5e9' : param.similarityScore >= 60 ? '#fff3e0' : '#f5f5f5',
                        border: `2px solid ${param.similarityScore >= 80 ? '#4caf50' : param.similarityScore >= 60 ? '#ff9800' : '#bdbdbd'}`
                      }}
                    >
                      <Typography
                        variant="body2"
                        fontWeight="bold"
                        color={param.similarityScore >= 80 ? 'success.main' : param.similarityScore >= 60 ? 'warning.main' : 'textSecondary'}
                      >
                        {param.similarityScore}%
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              )) : (
                <TableRow>
                  <TableCell colSpan={6}>
                    <Alert severity="info">{copy.noSimilarParameters}</Alert>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <Alert severity="info" icon={<InfoOutlinedIcon />} sx={{ mt: 2 }}>
          <Typography variant="caption">
            {copy.similarityNote}
          </Typography>
        </Alert>
        </Box>
      </Paper>
    </Box>
  );
};

export default DataExplorationDetail;
